"use client";

import { useEffect, useState } from "react";
import { useParams, useRouter } from "next/navigation";
import toast from "react-hot-toast";
import { logger } from "@/lib/logger";
import { createClient } from "@/lib/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { BottomNav } from "@/components/bottom-nav";
import {
  ArrowLeft,
  Loader2,
  MapPin,
  DollarSign,
  Calendar,
  Users,
  CheckCircle2,
  Save,
  Pencil,
  Eye,
} from "lucide-react";
import Link from "next/link";
import { TRADE_CATEGORIES } from "@/lib/constants";

const TRADE_LABEL: Record<string, string> = TRADE_CATEGORIES.reduce((acc, cat) => {
  acc[cat.id] = cat.label;
  return acc;
}, {} as Record<string, string>);

interface JobDetail {
  id: string;
  title: string;
  trade: string;
  suburb: string | null;
  site_address: string | null;
  description: string;
  rate_min: number | null;
  rate_max: number | null;
  rate_type: string | null;
  start_date: string | null;
  duration: string | null;
  workers_needed: number;
  status: string;
  created_at: string;
  certifications_required: string[];
}

export default function ContractorJobDetailPage() {
  const params = useParams();
  const router = useRouter();
  const jobId = params.jobId as string;
  const supabase = createClient();

  const [job, setJob] = useState<JobDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [rateMin, setRateMin] = useState("");
  const [rateMax, setRateMax] = useState("");
  const [rateType, setRateType] = useState<"hourly" | "daily">("hourly");
  const [workersNeeded, setWorkersNeeded] = useState("1");
  const [startDate, setStartDate] = useState("");
  const [duration, setDuration] = useState("");
  const [suburb, setSuburb] = useState("");
  const [siteAddress, setSiteAddress] = useState("");

  useEffect(() => {
    async function load() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: company } = await supabase
        .from("companies")
        .select("id")
        .eq("profile_id", user.id)
        .single();

      if (!company) { setLoading(false); return; }

      const { data: jobData, error } = await supabase
        .from("jobs")
        .select("*")
        .eq("id", jobId)
        .eq("company_id", company.id)
        .single();

      if (error || !jobData) {
        toast.error("Job not found");
        router.push("/contractor/my-jobs");
        return;
      }

      setJob(jobData as JobDetail);

      // Pre-fill edit fields
      setTitle(jobData.title || "");
      setDescription(jobData.description || "");
      setRateMin(jobData.rate_min ? String(jobData.rate_min) : "");
      setRateMax(jobData.rate_max ? String(jobData.rate_max) : "");
      setRateType((jobData.rate_type as "hourly" | "daily") || "hourly");
      setWorkersNeeded(String(jobData.workers_needed || 1));
      setStartDate(jobData.start_date || "");
      setDuration(jobData.duration || "");
      setSuburb(jobData.suburb || "");
      setSiteAddress(jobData.site_address || "");
      setLoading(false);
    }
    load();
  }, [jobId, supabase, router]);

  async function handleSave() {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);

    const { error } = await supabase
      .from("jobs")
      .update({
        title: title.trim(),
        description: description.trim(),
        rate_min: parseFloat(rateMin) || null,
        rate_max: parseFloat(rateMax) || parseFloat(rateMin) || null,
        rate_type: rateType,
        workers_needed: parseInt(workersNeeded) || 1,
        start_date: startDate || null,
        duration: duration || null,
        suburb: suburb || null,
        site_address: siteAddress || null,
      })
      .eq("id", jobId);

    if (error) {
      logger.error("Failed to update job:", error);
      toast.error("Failed to save changes");
    } else {
      toast.success("Job updated!");
      setEditing(false);
      // Refresh job data
      const { data: updated } = await supabase.from("jobs").select("*").eq("id", jobId).single();
      if (updated) setJob(updated as JobDetail);
    }
    setSaving(false);
  }

  async function handleStatusChange(newStatus: string) {
    const { error } = await supabase
      .from("jobs")
      .update({ status: newStatus })
      .eq("id", jobId);

    if (error) {
      toast.error("Failed to update status");
    } else {
      toast.success(`Job ${newStatus === "closed" ? "closed" : "reactivated"}!`);
      setJob(prev => prev ? { ...prev, status: newStatus } : null);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!job) return null;

  const rate = job.rate_min && job.rate_max && job.rate_min !== job.rate_max
    ? `$${job.rate_min}–$${job.rate_max}`
    : job.rate_min
    ? `$${job.rate_min}`
    : job.rate_max
    ? `$${job.rate_max}`
    : null;

  return (
    <div className="min-h-screen bg-background pb-20">
      <main className="mx-auto max-w-screen-sm px-4 py-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="icon" onClick={() => router.push("/contractor/my-jobs")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold flex-1 truncate">{job.title}</h1>
          {!editing && (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="mr-1.5 h-4 w-4" /> Edit
            </Button>
          )}
        </div>

        {editing ? (
          /* Edit mode */
          <div className="space-y-4">
            <div>
              <Label className="text-base">Title</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)} className="h-12 mt-1 text-base" />
            </div>

            <div>
              <Label className="text-base">Description</Label>
              <Textarea value={description} onChange={e => setDescription(e.target.value)} className="mt-1 text-base min-h-[120px]" />
            </div>

            <div>
              <Label className="text-base">Suburb</Label>
              <Input value={suburb} onChange={e => setSuburb(e.target.value)} className="h-12 mt-1 text-base" />
            </div>

            <div>
              <Label className="text-base">Site Address</Label>
              <Input value={siteAddress} onChange={e => setSiteAddress(e.target.value)} className="h-12 mt-1 text-base" placeholder="Optional" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-base">Pay Rate ($)</Label>
                <Input type="number" value={rateMin} onChange={e => { setRateMin(e.target.value); setRateMax(e.target.value); }} className="h-12 mt-1 text-base" />
              </div>
              <div>
                <Label className="text-base">Rate Type</Label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  <Button type="button" variant={rateType === "hourly" ? "default" : "outline"} onClick={() => setRateType("hourly")} className="h-12">
                    /hr
                  </Button>
                  <Button type="button" variant={rateType === "daily" ? "default" : "outline"} onClick={() => setRateType("daily")} className="h-12">
                    /day
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-base">Workers Needed</Label>
                <Input type="number" value={workersNeeded} onChange={e => setWorkersNeeded(e.target.value)} className="h-12 mt-1 text-base" min="1" />
              </div>
              <div>
                <Label className="text-base">Start Date</Label>
                <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-12 mt-1 text-base" />
              </div>
            </div>

            <div>
              <Label className="text-base">Duration</Label>
              <Input value={duration} onChange={e => setDuration(e.target.value)} className="h-12 mt-1 text-base" placeholder="e.g. 2 weeks, ongoing" />
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setEditing(false)} className="flex-1 h-12" disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSave} className="flex-1 h-12" disabled={saving}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save Changes
              </Button>
            </div>
          </div>
        ) : (
          /* View mode */
          <>
            <Card className="mb-4">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="secondary">{TRADE_LABEL[job.trade] || job.trade}</Badge>
                  <Badge className={job.status === "active" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}>
                    {job.status}
                  </Badge>
                </div>

                <p className="mt-3 text-sm text-muted-foreground whitespace-pre-wrap">{job.description}</p>

                <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm">
                  {job.suburb && (
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <MapPin className="h-4 w-4" /> {job.suburb}
                    </span>
                  )}
                  {job.site_address && (
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <MapPin className="h-4 w-4" /> {job.site_address}
                    </span>
                  )}
                  {rate && (
                    <span className="flex items-center gap-1.5 font-semibold">
                      <DollarSign className="h-4 w-4" /> {rate}{job.rate_type === "hourly" ? "/hr" : "/day"}
                    </span>
                  )}
                  {job.start_date && (
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      {new Date(job.start_date).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  )}
                  {job.duration && (
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <Calendar className="h-4 w-4" /> {job.duration}
                    </span>
                  )}
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <Users className="h-4 w-4" /> {job.workers_needed} worker{job.workers_needed > 1 ? "s" : ""} needed
                  </span>
                </div>

                {job.certifications_required?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {job.certifications_required.map(cert => (
                      <Badge key={cert} variant="outline" className="text-xs">{cert}</Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick actions */}
            <div className="space-y-3">
              <Button asChild className="w-full h-12 text-base">
                <Link href="/contractor/matches">
                  <Eye className="mr-2 h-5 w-5" /> View Matches
                </Link>
              </Button>

              {job.status === "active" && (
                <Button
                  variant="outline"
                  className="w-full h-12 text-base text-destructive hover:text-destructive"
                  onClick={() => handleStatusChange("closed")}
                >
                  Close Job Listing
                </Button>
              )}

              {job.status === "closed" && (
                <Button
                  variant="outline"
                  className="w-full h-12 text-base"
                  onClick={() => handleStatusChange("active")}
                >
                  <CheckCircle2 className="mr-2 h-5 w-5" /> Reactivate Job
                </Button>
              )}
            </div>

            <p className="mt-4 text-xs text-center text-muted-foreground">
              Posted {new Date(job.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          </>
        )}
      </main>
      <BottomNav userType="contractor" />
    </div>
  );
}
